/**
 * Form-local shape for `AiModelFormDialog`. Every field is a string/select
 * value so a text or number input can hold "empty" cleanly; `buildAiModelBody`
 * converts this into the nullable, typed request body the service expects.
 */

import type { AiModelCreate } from '@services/aiModelsService';

export type TriState = 'unknown' | 'true' | 'false';

export interface AiModelFormData {
  model_id: string;
  upstream: 'vertex' | 'local' | 'bedrock';
  api: 'anthropic' | 'gemini';
  upstream_model_id: string;
  provider: string;
  region: string;
  enabled: boolean;
  context_window: string;
  max_output_tokens: string;
  supports_prompt_caching: TriState;
  supports_extended_thinking: TriState;
}

export const EMPTY_AI_MODEL_FORM: AiModelFormData = {
  model_id: '',
  upstream: 'vertex',
  api: 'anthropic',
  upstream_model_id: '',
  provider: '',
  region: '',
  enabled: true,
  context_window: '',
  max_output_tokens: '',
  supports_prompt_caching: 'unknown',
  supports_extended_thinking: 'unknown',
};

function triStateToBoolean(value: TriState): boolean | null {
  if (value === 'unknown') return null;
  return value === 'true';
}

function booleanToTriState(value: boolean | null): TriState {
  if (value === null) return 'unknown';
  return value ? 'true' : 'false';
}

export function aiModelToFormData(model: {
  model_id: string;
  upstream: 'vertex' | 'local' | 'bedrock';
  api: 'anthropic' | 'gemini';
  upstream_model_id: string;
  provider: string;
  region: string | null;
  enabled: boolean;
  context_window: number | null;
  max_output_tokens: number | null;
  supports_prompt_caching: boolean | null;
  supports_extended_thinking: boolean | null;
}): AiModelFormData {
  return {
    model_id: model.model_id,
    upstream: model.upstream,
    api: model.api,
    upstream_model_id: model.upstream_model_id,
    provider: model.provider,
    region: model.region ?? '',
    enabled: model.enabled,
    context_window: model.context_window === null ? '' : String(model.context_window),
    max_output_tokens: model.max_output_tokens === null ? '' : String(model.max_output_tokens),
    supports_prompt_caching: booleanToTriState(model.supports_prompt_caching),
    supports_extended_thinking: booleanToTriState(model.supports_extended_thinking),
  };
}

/** Builds the request body sent to create/update; blank text means null. */
export function buildAiModelBody(form: AiModelFormData): Partial<AiModelCreate> {
  return {
    model_id: form.model_id,
    upstream: form.upstream,
    api: form.api,
    upstream_model_id: form.upstream_model_id,
    provider: form.provider,
    region: form.region.trim() === '' ? null : form.region.trim(),
    enabled: form.enabled,
    context_window: form.context_window.trim() === '' ? null : Number(form.context_window),
    max_output_tokens:
      form.max_output_tokens.trim() === '' ? null : Number(form.max_output_tokens),
    supports_prompt_caching: triStateToBoolean(form.supports_prompt_caching),
    supports_extended_thinking: triStateToBoolean(form.supports_extended_thinking),
  };
}
